import moment from 'moment';

import { convertMgToMmol } from './dataHelpers';
import { _config } from './envConfig';
import log from './log';
import { getSessionObject } from './storage';
import mediator from './mediator';
import { observeStore } from './store';
import i18n from './i18n';

import { DateFormats, TimeFormats } from '../constants/formatTypes';

let currentDateFormats = DateFormats['1'];
let currentTimeFormats = TimeFormats['1'];

const plainDateFormat = 'YYYY-MM-DD';

// This assumes that we will NEVER use milliseconds for timestamps
export const _isUnixTime = (value: any) => {
  // need these checks because the number check passes when passed in a jsDate or moment object
  if (value && (value._isAMomentObject || value.toLocaleTimeString)) {
    return false;
  }

  return Number.isInteger(Number(value)); // null computes to true here, undefined to false
};

export const _getMoment = (value: any) =>
  _isUnixTime(value) ? moment.unix(value) : moment(value);
export const _getMomentUTC = (value: any) => _getMoment(value).utc();

const changedDateFormat = (format: string) => {
  currentDateFormats = DateFormats[format || '1'];
};

const changedTimeFormat = (format: string) => {
  currentTimeFormats = TimeFormats[format || '1'];
};

/**
 * pass in an object with day, month, and year to get back a unix timestamp
 * day is optional
 */
export const createDateString = ({
  day,
  month,
  year,
}: {
  [key: string]: number | undefined;
}) => {
  if (month === undefined || year === undefined) {
    log.panic('createTimestamp: must pass in year and month');
    return '';
  }

  if (day === undefined) {
    return moment(`${year}-${month}-01`, 'YYYY-MM-DD').format('YYYY-MM-DD');
  } else {
    return moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').format('YYYY-MM-DD');
  }
};

export const createDateStringFromJsDate = (date: Date) => {
  return moment(date).format('YYYY-MM-DD');
};

export const createUnixFromJsDate = (date = new Date()) => {
  return moment(date).unix();
};

export const findHcpinPractices = (id: string, practices: any[] | any) => {
  if (!practices) {
    practices = getSessionObject('practices');
  }

  if (!Array.isArray(practices)) {
    practices = Object.values(practices);
  }

  for (const practice of practices) {
    const hcp = practice.careTeam.find((c: any) => c.id === id);

    if (hcp) {
      return hcp;
    }
  }

  return null;
};

export const dateToMidnightUTC = (date = moment()) =>
  moment.utc(_getMoment(date).format(plainDateFormat)).unix();

export const timestampNow = () => moment().unix();
export const timestampNowUTC = () => moment.utc().unix();

export const getCurrentUser = () => {
  return getSessionObject('userData');
};

export const mgToMmolIfNeeded = (value: string | number, uom: string) => {
  if (uom === '1') {
    return value;
  }

  return convertMgToMmol(value);
};

export const sortByTimestamp = <T extends { timestamp: number }>(
  a: T,
  b: T,
) => {
  const aDate = a.timestamp;
  const bDate = b.timestamp;

  return bDate - aDate;
};

export const getUnixTimstamp = (value = moment()) => _getMoment(value).unix();

/**
 * Generates a format based on user settings to be used for moment formatting
 *
 * @param {string} format Either a key corresponding to a custom date format
 * @param {string} [time] A time format option to add to the end
 * @param {string} [seperator] The seperator to use between date and time, otherwise a space is used
 */
export const makeFormatString = (format: string, time = '', seperator = '') => {
  let result = '';

  if (currentDateFormats[format]) {
    result += currentDateFormats[format];
  } else if (format) {
    return format;
  }

  if (currentTimeFormats[time]) {
    result += result.length > 0 ? (seperator ? ` ${seperator} ` : ' ') : '';
    result += currentTimeFormats[time];
  }

  return result;
};

/**
 * Formats a timestamp to the specified format type
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {string} format A date format type or compatible moment format string
 * @param {string} [time] A time format option to add to the end
 * @param {string} [seperator] The seperator to use between date and time, otherwise a space is used
 */
export const timeStampFormat = (
  value: any,
  format: string,
  time?: string,
  seperator?: string,
) => _getMoment(value).format(makeFormatString(format, time, seperator));

// Since we are calling this in one place we are just rogue casting moment instead of adding the function as a typing
const _createFromInputFallback = (moment as any).createFromInputFallback;

(moment as any).createFromInputFallback = (config: any) => {
  if (!_config.production) {
    log.error('Detected a momentjs fallback to new Date()!');
    alert('Detected a momentjs fallback to new Date()!');
  }

  _createFromInputFallback(config);
};

const valuesToMoment = ({ year, month = 1, day }: any) =>
  moment.utc({ year, month: Number(month) - 1, day });

/**
 * This will take an object of a date (just year, month, day properties) and return a UTC timestamp.
 * NOTE: this expects the month to be 1-12, not 0 indexed like moment expects. This function will subtract
 * 1 from the month value for you.
 *
 * @param {object} dateObj an object that has `year`, `month`, and `day` properties
 */
export const valuesToTimestampUTC = (dateObj: any) => {
  return valuesToMoment(dateObj).unix();
};

/**
 * Takes an object of year, month, and day and determines whether that is an actual date
 *
 * @param {*} dateObj Object with properties: `year`, `month`, `day`
 */
export const isDateFromPartsValid = (dateObj: any) =>
  // Moment considers a day value of 0 valid, so this check ensures we don't accept 0 as a valid day
  dateObj.day !== 0 &&
  valuesToMoment(dateObj).isValid() &&
  // Ensure values are whole numbers
  Number.isInteger(Number(dateObj.year)) &&
  Number.isInteger(Number(dateObj.month)) &&
  (dateObj.day === undefined || Number.isInteger(Number(dateObj.day)));

/**
 * Returns true if the given previous date's timestamp is before the given future date's timestamp
 * Future date will default to a current timestamp if no param is given
 *
 * @param {object|number|string} previousDate - Timestamp string/number or object with 3 fields: month, day, year.
 * @param {object|number|string} futureDate - Timestamp string/number or object with 3 fields: month, day, year.
 */
export const isFutureDate = (
  previousDate: any,
  futureDate: any = timestampNowUTC(),
) => {
  // verify given dates are in timestamp format
  if (!Number.isInteger(previousDate)) {
    previousDate = isDateFromPartsValid(previousDate)
      ? valuesToTimestampUTC(previousDate)
      : _getMoment(previousDate).unix();
  }

  if (!Number.isInteger(futureDate)) {
    futureDate = isDateFromPartsValid(futureDate)
      ? valuesToTimestampUTC(futureDate)
      : _getMoment(futureDate).unix();
  }

  return previousDate > futureDate;
};

/**
 * Converts an enum to choice options format for DynamicForm
 * @param enumObj The enum object (e.g., EntityStatus)
 * @param translationPrefix The translation key prefix (e.g., 'Status')
 * @param options Optional filtering options
 * @returns Array of choice options with key and value
 */
export const enumToChoiceOptions = (
  enumObj: Record<string, any>,
  translationPrefix: string,
  options?: {
    exclude?: (string | number)[];
    include?: (string | number)[];
  },
) => {
  const choiceOptions = Object.entries(enumObj).reduce<
    Array<{ key: string; value: string }>
  >((choiceOptions, [key, value]) => {
    // skip reverse mappings from numeric enums, otherwise both the key and value are included
    if (!isNaN(Number(key))) {
      return choiceOptions;
    }

    // apply the include/exclude filtering if provided
    if (options?.include) {
      if (!options.include.includes(value) && !options.include.includes(key)) {
        return choiceOptions;
      }
    } else if (options?.exclude) {
      if (options.exclude.includes(value) || options.exclude.includes(key)) {
        return choiceOptions;
      }
    }

    // add value to the choice options array
    choiceOptions.push({
      key: String(value),
      value: i18n.t(`${translationPrefix}.${key.toLowerCase()}`),
    });

    return choiceOptions;
  }, []);

  return choiceOptions;
};

/**
 * Converts an enum to checkbox filter options format
 * @param enumObj The enum object (e.g., EntityStatus)
 * @param translationPrefix The translation key prefix (e.g., 'Status')
 * @param options Optional filtering options
 * @returns Array of checkbox filter items with key, value, and checked
 */
export const enumToCheckboxOptions = (
  enumObj: Record<string, any>,
  translationPrefix: string,
  options?: {
    exclude?: (string | number)[];
    include?: (string | number)[];
  },
) => {
  return enumToChoiceOptions(enumObj, translationPrefix, options).map(
    (option) => ({
      id: option.key,
      label: option.value,
      value: false,
    }),
  );
};

// Setup observers
mediator.once('store:created', () => {
  observeStore((state) => state.user.dateFormat, changedDateFormat);
  observeStore((state) => state.user.timeFormat, changedTimeFormat);
});
